import React, { useState, useEffect, useCallback } from 'react';
import { api } from '@/lib/api';
import { logger } from '@/lib/logger';
import { Spinner, Steps, Sparkle, CaretDown, Buildings, Timer } from '@phosphor-icons/react';
import { RemedyBuilderSection } from '../components/RemedyBuilderSection';

const LEVEL_META = {
  internal: { label: 'Internal', color: 'bg-[#F3F4F6] text-[#6B7280] border-[#E5E7EB]' },
  administrative: { label: 'Administrative', color: 'bg-blue-50 text-blue-700 border-blue-200' },
  regulatory: { label: 'Regulatory', color: 'bg-amber-50 text-amber-700 border-amber-200' },
  legal: { label: 'Legal', color: 'bg-red-50 text-red-700 border-red-200' },
};

const STEP_STATUS_META = {
  pending: { label: 'Pending', color: 'bg-white text-[#6B7280] border-[#E5E7EB]' },
  in_progress: { label: 'In progress', color: 'bg-blue-50 text-blue-700 border-blue-200' },
  done: { label: 'Done', color: 'bg-green-50 text-green-700 border-green-200' },
  skipped: { label: 'Skipped', color: 'bg-[#F3F4F6] text-[#94A3B8] border-[#E5E7EB]' },
};
const STEP_STATUS_ORDER = ['pending', 'in_progress', 'done', 'skipped'];

const StepStatusDropdown = ({ index, status, onChange }) => {
  const [open, setOpen] = useState(false);
  const meta = STEP_STATUS_META[status] || STEP_STATUS_META.pending;
  return (
    <div className="relative shrink-0">
      <button data-testid={`escalation-step-status-btn-${index}`} onClick={() => setOpen(o => !o)}
        className={`flex items-center gap-1 text-[11px] font-ibm font-semibold px-2 py-0.5 rounded border ${meta.color}`}>
        {meta.label} <CaretDown size={10} />
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-10" onClick={() => setOpen(false)} />
          <div className="absolute right-0 mt-1 z-20 bg-white border border-[#E5E7EB] rounded-md shadow-lg py-1 w-36" data-testid={`escalation-step-status-menu-${index}`}>
            {STEP_STATUS_ORDER.map(s => (
              <button key={s} data-testid={`escalation-step-status-opt-${s}-${index}`}
                onClick={() => { setOpen(false); if (s !== status) onChange(index, s); }}
                className={`w-full text-left px-3 py-1.5 text-xs font-ibm hover:bg-[#F3F4F6] ${s === status ? 'font-semibold text-[#002FA7]' : 'text-[#6B7280]'}`}>
                {STEP_STATUS_META[s].label}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

const StepCard = ({ step, index, onStatus }) => {
  const lvl = LEVEL_META[step.level] || LEVEL_META.administrative;
  const done = step.status === 'done';
  return (
    <div className={`bg-white border border-[#E5E7EB] rounded-lg p-4 ${step.status === 'skipped' ? 'opacity-60' : ''}`} data-testid={`escalation-step-${index}`}>
      <div className="flex items-start gap-3">
        <span className={`w-7 h-7 rounded-full flex items-center justify-center font-outfit text-sm font-bold shrink-0 ${done ? 'bg-green-100 text-green-700' : 'bg-[#002FA7]/10 text-[#002FA7]'}`}>{index + 1}</span>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h4 className="text-sm font-ibm font-semibold text-[#09090B]" data-testid={`escalation-step-title-${index}`}>{step.title}</h4>
            <span className={`text-[10px] font-ibm font-semibold px-1.5 py-0.5 rounded border ${lvl.color}`}>{lvl.label}</span>
          </div>
          {step.venue && (
            <p className="text-xs text-[#6B7280] font-ibm mt-1 flex items-center gap-1">
              <Buildings size={12} className="shrink-0" /> {step.venue}
            </p>
          )}
          {step.description && <p className="text-xs text-[#6B7280] font-ibm mt-1.5 leading-relaxed">{step.description}</p>}
          {(step.actions || []).length > 0 && (
            <ul className="mt-2 space-y-1">
              {step.actions.map((a, i) => (
                <li key={`step-${index}-a-${i}`} className="text-xs font-ibm text-[#09090B] flex items-start gap-1.5">
                  <span className="w-1 h-1 rounded-full bg-[#002FA7] mt-1.5 shrink-0" /> {a}
                </li>
              ))}
            </ul>
          )}
          {step.timeframe && (
            <p className="text-[11px] text-[#94A3B8] font-ibm italic mt-2 flex items-center gap-1">
              <Timer size={12} className="shrink-0" /> {step.timeframe}
            </p>
          )}
        </div>
        <StepStatusDropdown index={index} status={step.status} onChange={onStatus} />
      </div>
    </div>
  );
};

export const EscalationTab = ({ caseId }) => {
  const [plan, setPlan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/cases/${caseId}/escalation`);
      setPlan(data.plan);
    } catch (err) {
      logger.error('failed to load escalation plan:', err);
    } finally {
      setLoading(false);
    }
  }, [caseId]);

  useEffect(() => { load(); }, [load]);

  const generate = async () => {
    setGenerating(true);
    try {
      const { data } = await api.post(`/cases/${caseId}/escalation/generate`);
      setPlan(data.plan);
    } catch (err) {
      logger.error('failed to generate escalation plan:', err);
      alert('Failed to build the escalation plan. Make sure documents have been analyzed first.');
    } finally {
      setGenerating(false);
    }
  };

  const setStepStatus = async (index, status) => {
    setPlan(prev => ({ ...prev, steps: prev.steps.map((s, i) => (i === index ? { ...s, status } : s)) }));
    try {
      await api.patch(`/cases/${caseId}/escalation/steps/${index}`, { status });
    } catch (err) {
      logger.error('failed to update step status:', err);
      load();
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center py-20" data-testid="escalation-tab"><Spinner size={28} className="animate-spin text-[#002FA7]" /></div>;
  }

  return (
    <div data-testid="escalation-tab" className="space-y-5">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h3 className="font-outfit text-lg font-semibold text-[#09090B] flex items-center gap-2">
            <Steps size={20} className="text-[#002FA7]" weight="fill" /> Escalation Navigator
          </h3>
          <p className="text-sm text-[#6B7280] font-ibm mt-0.5 max-w-2xl">
            A step-by-step path for where to take this case next — from informal resolution up to regulatory and legal channels — plus the remedies your evidence supports demanding.
          </p>
        </div>
        <button onClick={generate} disabled={generating} data-testid="escalation-generate-button"
          className="flex items-center gap-1.5 px-4 py-2 bg-[#002FA7] text-white font-ibm font-semibold rounded-md text-sm hover:bg-[#00237D] disabled:opacity-50 transition-all shrink-0">
          {generating ? <><Spinner size={14} className="animate-spin" /> Building plan…</> : <><Sparkle size={14} weight="fill" /> {plan ? 'Rebuild Plan' : 'Build Escalation Plan'}</>}
        </button>
      </div>

      {!plan ? (
        <div className="text-center py-14 bg-white border border-dashed border-[#E5E7EB] rounded-lg" data-testid="escalation-empty">
          <Steps size={40} className="mx-auto text-[#002FA7]/40 mb-3" weight="fill" />
          <p className="text-[#6B7280] font-ibm max-w-lg mx-auto">No escalation plan yet. Click <span className="font-semibold text-[#002FA7]">Build Escalation Plan</span> to have the AI map out where to escalate this case — grounded in your documents, issue tree and applicable-laws panel.</p>
        </div>
      ) : (
        <>
          {plan.strategy_summary && (
            <div className="bg-white border border-[#E5E7EB] rounded-lg p-5" data-testid="escalation-strategy">
              <h4 className="text-xs font-bold tracking-[0.15em] uppercase text-[#6B7280] font-ibm mb-2">Strategy</h4>
              <p className="text-sm text-[#09090B] font-ibm leading-relaxed">{plan.strategy_summary}</p>
            </div>
          )}
          <div className="space-y-3" data-testid="escalation-steps">
            {(plan.steps || []).map((s, i) => <StepCard key={`step-${i}`} step={s} index={i} onStatus={setStepStatus} />)}
          </div>
          <RemedyBuilderSection caseId={caseId} plan={plan} onPlanChange={setPlan} />
        </>
      )}
    </div>
  );
};
